import {
    BoxGeometry,
    BufferGeometry, ColorRepresentation,
    Group,
    InstancedMesh, Line, LineBasicMaterial,
    Material,
    Matrix4,
    Mesh,
    MeshBasicMaterial,
    MeshPhongMaterial,
    MeshPhysicalMaterial,
    Object3D,
    Vector3
} from "three";
import { Generic3DModel } from "../models/generic-3D-model";
import { ResourceManagerService } from "../service/resource.manager.service";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { CustomMaterialType } from "../service/material/custom-material-type.enum";
import { MaterialService } from "../service/material/material.service";
import { Features } from "../decorators/features.decorator";
import { Movable } from "../models/feature/movable.feature";
import { dispose3dResource } from "../util/dispose-object";
import {disposeMaterial} from "../util/dispose-content";
import {ColorInfo} from "../../core/util/color-converter";
import { CSG } from 'three-csg-ts'
import {Object3DEventMap} from "three/src/core/Object3D";

@Features(Movable)
export class MountingProfileObject extends Generic3DModel<Group> {

    private data: any;
    private readonly hardCodedModelPath: string = "./assets/3d-files/mounting-profile-26.obj";
    private readonly pieceHeight = 53;
    private readonly useShadows = true;

    public material: Material | null = null;
    public mountingProfileInstancedMesh!: InstancedMesh;
    private partialMesh: Mesh<BufferGeometry, Material | Material[], Object3DEventMap> = null;

    constructor(
        data: any,
        private resourceManager: ResourceManagerService,
        private materialService: MaterialService
    ) {
        super('MountingProfileObject', new Group());
        this.data = { ...data };
    }

    public async init(): Promise<void> {
        try {
            const model = await this.loadModel(this.hardCodedModelPath);
            const { geometry } = this.extractCombinedGeometry(model);

            if (geometry) {
                const requiredLength: number = this.data.length ?? this.pieceHeight;
                //const count = Math.ceil(requiredLength / this.pieceHeight);

                const fullCount  = Math.floor(requiredLength / this.pieceHeight);
                const remainder  = requiredLength % this.pieceHeight;
                const count      = fullCount;                 // instansoidaan vain täydet palat

                // Placeholder materiaali
                this.material = new MeshBasicMaterial({
                    color: 0x00ff00, // vihreä väri
                });

                if (count > 0) {                              // ei luoda, jos vain vajaapala
                    this.mountingProfileInstancedMesh = new InstancedMesh(geometry, this.material, count);
                    this.mountingProfileInstancedMesh.castShadow    = this.useShadows;
                    this.mountingProfileInstancedMesh.receiveShadow = this.useShadows;

                    for (let i = 0; i < count; i++) {
                        const position = new Vector3(0, (i * this.pieceHeight) / 1000, 0);
                        const matrix   = new Matrix4().setPosition(position);
                        this.mountingProfileInstancedMesh.setMatrixAt(i, matrix);
                    }
                    this.mountingProfileInstancedMesh.instanceMatrix.needsUpdate = true;
                    this._object.add(this.mountingProfileInstancedMesh);
                }

                if (remainder > 0) {
                    // 1) kloonaa perusgeometria erilliseksi meshiksi
                    const baseMesh = new Mesh(geometry.clone(), this.material);

                    // 2) leikkaava boksi: korkeus = (pieceHeight - remainder)
                    const cutHeightMm = this.pieceHeight - remainder;

                    //  ── KORJAUS: varmista boundingBox ennen käyttöä ──
                    if (!geometry.boundingBox) geometry.computeBoundingBox();
                    const bbox = geometry.boundingBox;              // tyyppi on nyt Box3

                    const sizeX = (bbox.max.x - bbox.min.x) * 2;
                    const sizeZ = (bbox.max.z - bbox.min.z) * 2;

                    const cutter = new Mesh(new BoxGeometry(sizeX, cutHeightMm / 1000, sizeZ));


                    cutter.position.set(
                        0,
                        this.pieceHeight/1000-cutHeightMm / 1000/2,
                        0
                    );

                    cutter.updateMatrix();          // kirjoita position -> matrix
                    baseMesh.updateMatrix();

                    // laatikon keskipiste jäännöskorkeuden yläpuolelle
                    //cutter.position.set(0, remainder / 1000 + cutHeightMm / 1000 / 2, 0);

                    // 3) CSG-vähennys
                    // @ts-ignore
                    const trimmed = CSG.fromMesh(baseMesh).subtract(CSG.fromMesh(cutter)).toMesh(baseMesh.matrix, baseMesh.material);

                    //trimmed.geometry.translate(0, -cutHeightMm / 1000, 0);

                    trimmed.castShadow = trimmed.receiveShadow = this.useShadows;

                    // sijoita oikealle korkeudelle
                    trimmed.position.setY((fullCount * this.pieceHeight) / 1000);

                    // talteen ja sceneen
                    // @ts-ignore
                    this.partialMesh = trimmed;
                    // @ts-ignore
                    this._object.add(trimmed);
                }

                /*this.mountingProfileInstancedMesh = new InstancedMesh(geometry, this.material, count);
                this.mountingProfileInstancedMesh.castShadow = this.useShadows;
                this.mountingProfileInstancedMesh.receiveShadow = this.useShadows;

                for (let i = 0; i < count; i++) {
                    const position = new Vector3(0, (i * this.pieceHeight) / 1000, 0);
                    const matrix = new Matrix4().setPosition(position);
                    this.mountingProfileInstancedMesh.setMatrixAt(i, matrix);
                }*/

                this.mountingProfileInstancedMesh.instanceMatrix.needsUpdate = true;
                this._object.add(this.mountingProfileInstancedMesh);

                // Lisätään apuviivat jokaisen instanssin väliin.
                // Luodaan viiva kunkin parin välille instanssien välillä.
                /*for (let i = 0; i < count; i++) {
                    // Lasketaan instanssin y-koordinaatti
                    const y = (i * this.pieceHeight) / 1000;
                    // Viivan päätepisteet: vaakasuunnassa x = -1 ja x = 1, sama y-arvo
                    const pos1 = new Vector3(-1, y, 0);
                    const pos2 = new Vector3(1, y, 0);
                    const lineGeometry = new BufferGeometry().setFromPoints([pos1, pos2]);
                    const lineMaterial = new LineBasicMaterial({ color: 0xffff00 }); // punainen viiva
                    const line = new Line(lineGeometry, lineMaterial);
                    this._object.add(line);
                }*/

                const materialTypeEnum = this.data.materialType === "Powder Coated" ? CustomMaterialType.PowderCoated : CustomMaterialType.AnodizedAluminum;
                const color = this.data.color;
                await this.applyMaterialAndColor(materialTypeEnum, color);
            } else {
                console.error("Ladattu malli ei ole Mesh-tyyppinen, joten instansointia ei voida käyttää.");
            }
        } catch (error) {
            console.error("Failed to load model:", error);
        }
    }

    private async loadModel(path: string): Promise<Object3D | null> {
        return new Promise((resolve, reject)=> {
            this.resourceManager.loadResource(
                path,
                "obj",
                this.data.uuid,
                true
            )
                .then( (model) => {

                    if (model instanceof Object3D) {
                        resolve(model);
                    } else {
                        console.warn(`Model ${path} not loaded correctly`);
                        resolve(null);
                    }

                })
                .catch(reject);
        });
    }

    public object() {
        return this._object;
    }

    /**
     * Yhdistää (merge) Mesh-geometriat annetusta Object3D:stä.
     */
    private extractCombinedGeometry(
        object: Object3D | undefined
    ): { geometry: BufferGeometry | null; material: Material | null } {
        if (!object) {
            return { geometry: null, material: null };
        }

        const geometries: BufferGeometry[] = [];
        let material: Material | null = null;

        object.updateMatrixWorld(true);
        object.traverse((child) => {
            if (child instanceof Mesh && child.geometry) {
                child.updateMatrixWorld(true);
                const cloned = child.geometry.clone();
                cloned.applyMatrix4(child.matrixWorld);
                geometries.push(cloned);
            }
        });

        if (geometries.length === 0) {
            return { geometry: null, material: null };
        }

        const mergedGeometry = mergeGeometries(geometries);
        return { geometry: mergedGeometry, material };
    }

    /**
     * Asettaa materiaalin värin, mikäli materiaali tukee color-ominaisuutta.
     * @param color
     */
    public setColor(color: ColorInfo): void {
        if (!this.mountingProfileInstancedMesh) { return; }

        this.data.color = color; //TODO: unwrap data attribute from this object
        // Rajataan materiaalin tyyppi, jotta saadaan käyttöön 'color' ja 'needsUpdate'
        const mat = this.mountingProfileInstancedMesh.material as MeshBasicMaterial | MeshPhongMaterial | MeshPhysicalMaterial;
        mat.color.set(color.HEX);
        mat.needsUpdate = true;
    }

    /**
     * Hakee uuden materiaalin, disposaa vanhan ja asettaa uuden materiaalin.
     * @param materialType Materiaalin tyyppi (esim. "Powder Coated")
     * @param color        Objekti, jonka HEX-kenttä sisältää väriarvon (esim. { HEX: "#ff0000" })
     */
    public async applyMaterialAndColor(
        materialType: CustomMaterialType,
        color: ColorInfo
    ): Promise<void> {

        /* 1) Tallenna tiedot */
        this.data.color = color;
        this.data.materialType = materialType;

        /* 2) Nouda ja väritä uusi materiaali */
        const fetchedMaterial = await this.materialService.getMaterial(materialType);
        (fetchedMaterial as MeshBasicMaterial | MeshPhongMaterial | MeshPhysicalMaterial)
            ?.color?.set(color.HEX);

        /* 3) Päivitä instanssit (jos niitä on) */
        if (this.mountingProfileInstancedMesh) {
            Array.isArray(this.mountingProfileInstancedMesh.material)
                ? this.mountingProfileInstancedMesh.material.forEach(disposeMaterial)
                : disposeMaterial(this.mountingProfileInstancedMesh.material);

            this.mountingProfileInstancedMesh.material = fetchedMaterial;
            if ("needsUpdate" in this.mountingProfileInstancedMesh.material) {
                (this.mountingProfileInstancedMesh.material as MeshBasicMaterial
                    | MeshPhongMaterial | MeshPhysicalMaterial).needsUpdate = true;
            }
        }

        /* 4) Päivitä vajaapala (jos se on olemassa) */
        if (this.partialMesh) {
            //disposeMaterial(this.partialMesh.material);
            this.partialMesh.material = fetchedMaterial;

            if ("needsUpdate" in this.partialMesh.material) {
                (this.partialMesh.material as MeshBasicMaterial
                    | MeshPhongMaterial | MeshPhysicalMaterial).needsUpdate = true;
            }
        }

        /* 5) Tallenna referenssi */
        this.material = fetchedMaterial;
    }

    /**
     * Vapauttaa kaikki MountingProfileObjectin käytössä olevat three.js-resurssit.
     */
    public override destroy(): void {

        // Tyhjennetään viite, jotta garbage collectori voi kerätä sen
        this.mountingProfileInstancedMesh = undefined as any;

        // Tyhjennetään mahdolliset materiaaliviitteet
        this.material = null;

        dispose3dResource(this._object);

        this.destroyed.next();
    }

}
